import { useCallback, useEffect, useMemo } from "react"
import { Routes, Route } from "react-router-dom"
import _ from "lodash"
import { useIdleTimer } from "react-idle-timer"
import { useNavigate } from "react-router-dom"
import { AnimatePresence } from "framer-motion"

import config from "config"
import { DEVICE_KIOSK_V } from "constant"

import {
  set as setPersistValue,
  get as getPersistValue,
} from "utils/persist_store"
import {
  homeScreenModeWithElectron,
  updateAppStatus,
  APP_STATUS,
  homeScreenModeWebApp,
} from "utils/electron"

import { useVenue } from "providers/venue"
import { useAppUI } from "providers/ui"

import { useSession } from "hooks/analytics/useSession"

// Extensions
import { extensionConfigs } from "extensions"
import {
  getAllExtensionRoutes,
  getExtensionAppComponentConfig,
  getExtensionAppLayoutConfig,
  getExtensionThemeFactories,
  mergeRoutes,
  registerRouteByConfig,
} from "utils/extension"

// Theme
import { generateTheme } from "theme/generateTheme"
import { appThemeOptionFactory as directoryThemeOptionFactory } from "theme/app/directory"
import { ThemeProvider } from "providers/mui-extended/ThemeProvider"

// Routes
import { standardRoutes } from "standard"

// Pages
import { Layout as MapLayout } from "pages/maps/_layout.kiosk-v"

// Components, Layouts
import { AppLayout, PageLayout } from "layouts/kiosk-v"
import { ScrollToTop } from "components/ScrollToTop"
import { LoadingScreen, MapUpdatingScreen } from "components/LoadingScreen"
import { MotionDiv, showContent } from "components/motions"
import { createIndexRoute } from "providers/route/utils/createIndexRoute"
import { MapIndex } from "pages/maps"
import { createTheme } from "@mui/material"

const resetEvent = new Event("appreset")

// Extension routes
const extensionRoutes = getAllExtensionRoutes(
  config("venueExtension"),
  extensionConfigs,
  DEVICE_KIOSK_V
)

// Merge Standard + Extension routes
const { pageRoutes, mapRoutes, baseRoutes } = mergeRoutes(
  _.get(standardRoutes, DEVICE_KIOSK_V),
  extensionRoutes
)

// Extension Themes
const extensionThemeConfig = getExtensionThemeFactories(
  config("venueExtension"),
  extensionConfigs,
  DEVICE_KIOSK_V
)

// Extension App Layout Config
const result = getExtensionAppLayoutConfig(
  config("venueExtension"),
  extensionConfigs,
  DEVICE_KIOSK_V
)
const { search, appNavigation } = result

// Extension App Components Config
const appComponentConfig = getExtensionAppComponentConfig(
  config("venueExtension"),
  extensionConfigs,
  DEVICE_KIOSK_V
)
const { loadingScreen, preservedView } = appComponentConfig

// App Theme Factory
const appThemeOptionFactory = _.get(directoryThemeOptionFactory, DEVICE_KIOSK_V)

export const KioskVApp = () => {
  // Generate a venue theme
  const {
    themeOptions: venueThemeOptions,
    // refetchImdfData,
    dataLoaded,
    indexPath,
    idleModeConfig,
  } = useVenue()
  const { redirectPath, ...useIdleTimerProps } = idleModeConfig

  const {
    INITIAL_SESSION_KEY,
    gtmPushDataLayerCountKioskUser,
    gtmPushSurveyNewSession,
  } = useSession()

  const {
    mapReady,
    mapLoading,
    setWheelchairModeEnabled,
    updateRouteOptions,
    themeDirection,
  } = useAppUI()
  const theme = generateTheme(venueThemeOptions, appThemeOptionFactory, {
    extendThemeFactories: extensionThemeConfig.themeFactories,
    ignoreAppThemeFactory: extensionThemeConfig.ignoreAppThemeFactory,
    ignoreDeviceThemeFactory: extensionThemeConfig.ignoreDeviceThemeFactory,
  })
  const navigate = useNavigate()
  const isAppReady = useMemo(
    () => mapReady && dataLoaded,
    [mapReady, dataLoaded]
  )
  const onIdle = useCallback(async () => {
    setWheelchairModeEnabled(false)
    navigate(redirectPath)
    window.dispatchEvent(resetEvent)
    gtmPushSurveyNewSession()
    homeScreenModeWithElectron()
    updateAppStatus(APP_STATUS.IDLE)
    homeScreenModeWebApp(true)
    // Refetch IMDF data on idle
    // await refetchImdfData()
  }, [
    redirectPath,
    setWheelchairModeEnabled,
    gtmPushSurveyNewSession,
    navigate /* , refetchImdfData */,
  ])

  const onActive = useCallback(() => {
    gtmPushDataLayerCountKioskUser()
  }, [gtmPushDataLayerCountKioskUser])

  const onAction = useCallback(() => {
    if (!getPersistValue(INITIAL_SESSION_KEY)) {
      gtmPushDataLayerCountKioskUser()
    }
    setPersistValue(INITIAL_SESSION_KEY, true)
  }, [INITIAL_SESSION_KEY, gtmPushDataLayerCountKioskUser])

  useIdleTimer({
    onActive,
    onAction,
    onIdle,
    timeout: 10000,
    throttle: 500,
    ...useIdleTimerProps,
  })

  useEffect(() => {
    updateRouteOptions({ baseRoutes, pageRoutes, mapRoutes })
  }, [updateRouteOptions])

  const themeWithDirection = useMemo(
    () =>
      createTheme(theme, {
        direction: themeDirection,
        isRtl: themeDirection === "rtl",
      }),
    [theme, themeDirection]
  )

  return (
    <ThemeProvider theme={themeWithDirection}>
      <ScrollToTop />
      {isAppReady ? preservedView : null}
      <AnimatePresence mode="sync">
        {!isAppReady
          ? loadingScreen || <LoadingScreen key="loading-screen" />
          : null}
        <MapUpdatingScreen key="map-updating" open={mapLoading} />
        <MotionDiv
          key="content"
          variants={showContent}
          initial="initial"
          animate="animate"
        >
          <Routes>
            <Route
              path="/"
              element={
                <AppLayout search={search} appNavigation={appNavigation} />
              }
            >
              {createIndexRoute({ to: indexPath })}
              {baseRoutes.map(registerRouteByConfig)}
              <Route path="/*" element={<PageLayout />}>
                {/* Add or override specific route extension pages here. */}
                {pageRoutes.map(registerRouteByConfig)}
              </Route>

              <Route path="/maps/*" element={<MapLayout />}>
                <Route index element={<MapIndex />} />
                {/* The indoorPage parent router treats the route child parameter as the same parameter as its own. */}
                {mapRoutes.map(registerRouteByConfig)}
              </Route>
            </Route>
          </Routes>
        </MotionDiv>
      </AnimatePresence>
    </ThemeProvider>
  )
}
